import React from 'react';
import { Button } from '../../../components/ui/Button';
type PanelProps = {
  title: string;
  description: string;
  children: React.ReactNode;
  dirty?: boolean;
  saving?: boolean;
  status?: {
    ok: boolean;
    text: string;
  } | null;
  canEdit?: boolean;
  onSave?: () => void;
  onReset?: () => void;
};

/** Settings section card with a sticky save bar. Read-only for roles without settings permission. */
export function SettingsPanel({
  title,
  description,
  children,
  dirty,
  saving,
  status,
  canEdit = true,
  onSave,
  onReset
}: PanelProps) {
  return <section className="rounded-lg border border-slate-200 bg-white" aria-labelledby={`sp-${title}`} data-id="element-5564" data-mp="w7i5t33y04yq">
      <header className="border-b border-slate-100 px-5 py-4" data-id="element-5565" data-mp="1150hh19cgyaf">
        <h2 id={`sp-${title}`} className="text-base font-bold text-slate-900" data-id="element-5566" data-mp="15y3cldtrsfsq">{title}</h2>
        <p className="text-sm text-slate-500" data-id="element-5567" data-mp="8ifqw30p0q7x">{description}</p>
        {!canEdit && <p className="mt-2 rounded-md bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-5568" data-mp="dgqwg1jygekc">View only — your role cannot change these settings.</p>}
      </header>
      <fieldset disabled={!canEdit} className="space-y-5 px-5 py-4" data-id="element-5569" data-mp="25dgit6uf6xi5">{children}</fieldset>
      {onSave && canEdit && <footer className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3" data-id="element-5570" data-mp="21ehbxgp4oqo2">
          {status && <p role={status.ok ? 'status' : 'alert'} className={`mr-auto text-sm ${status.ok ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-5571" data-mp="1rlidz87qrnrs">{status.text}</p>}
          {!status && dirty && <p className="mr-auto text-sm text-amber-700" data-id="element-5572" data-mp="1honheuv13xg5">Unsaved changes</p>}
          <Button variant="ghost" onClick={onReset} disabled={!dirty || saving} data-id="element-5573" data-mp="1hqi0e3mqpfhl">Discard</Button>
          <Button onClick={onSave} isLoading={saving} disabled={!dirty} data-id="element-5574" data-mp="1dowksaitmn3p">Save</Button>
        </footer>}
    </section>;
}
export const inputCls = 'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500';
export function Field({
  id,
  label,
  hint,
  children,
  wide
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return <div className={wide ? 'sm:col-span-2' : ''} data-id="element-5575" data-mp="3qywomp6ryhct">
      <label htmlFor={id} className="text-sm font-medium text-slate-700" data-id="element-5576" data-mp="3d6q995bg4eef">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-500" data-id="element-5577" data-mp="3i59rovqtz821">{hint}</p>}
    </div>;
}
export function Toggle({
  id,
  label,
  hint,
  checked,
  onChange
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <label htmlFor={id} className="flex items-start gap-3 text-sm" data-id="element-5578" data-mp="3vwewec922qj8">
      <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600" data-id="element-5579" data-mp="3vy98r269habp" />
      <span data-id="element-5580" data-mp="2kg86k5emwf1j"><span className="font-medium text-slate-800" data-id="element-5581" data-mp="2kfeucowwoxc0">{label}</span>{hint && <span className="block text-xs text-slate-500" data-id="element-5582" data-mp="2pgm5mxy9cenf">{hint}</span>}</span>
    </label>;
}

/** Editable list of short values (categories, units…). */
export function ListEditor({
  id,
  label,
  values,
  onChange,
  placeholder
}: {
  id: string;
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [text, setText] = React.useState('');
  const add = () => {
    const v = text.trim();
    if (!v || values.some(x => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...values, v]);
    setText('');
  };
  return <div data-id="element-5583" data-mp="1y6hrggmgn49s">
      <p className="text-sm font-medium text-slate-700" data-id="element-5584" data-mp="1u6zatx1o9fcr">{label}</p>
      <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-5585" data-mp="1u8k2newlqk6x">
        {values.map(v => <li key={v} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs font-medium text-slate-700" data-id="element-5586" data-mp="27z5pfep6fea3">
            {v}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="rounded-full px-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700" aria-label={`Remove ${v}`} data-id="element-5587" data-mp="1fhtau6v0fl3t">×</button>
          </li>)}
      </ul>
      <div className="mt-2 flex gap-2" data-id="element-5588" data-mp="1o7a49x8efwv8">
        <label htmlFor={id} className="sr-only" data-id="element-5589" data-mp="1t4tjp0gu2for">Add to {label}</label>
        <input id={id} value={text} onChange={e => setText(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          add();
        }
      }} placeholder={placeholder} className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm" data-id="element-5590" data-mp="1t53bmtlfuwx3" />
        <Button size="sm" variant="outline" onClick={add} data-id="element-5591" data-mp="26wzvybgv82ix">Add</Button>
      </div>
    </div>;
}